/**
 * @desc        Index.js
 * @version     1.0.1 (Fixed window.ResizeObserver loop completed with undelivered notifications) - 15 May 2026
 * */

import React from 'react'
import * as Sentry from '@sentry/react'
// import { StrictMode } from 'react'
// import ReactDOM from 'react-dom'
// import './api/axiosDeplication.js'
import { Provider } from 'react-redux'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { PersistGate } from 'redux-persist/integration/react'
import { store, persistor } from './redux/store.js'
import { WEBAPP_VERSION, APP_IOS_VERSION, APP_ANDROID_VERSION, LAST_MODIFIED_TIMESTAMP } from './version.js'
// import './index.css' // TODO: review this one
import App from './App'
import { MAINTENANCE_MODE } from 'app-config/config.js'
import SiteUpgradingScreen from './components/site-upgrading-screen.js'
import { configureInterceptors } from './api/interceptor.js'
import { createRoot } from 'react-dom/client'
import { createFirebaseInstance } from 'services/firebase/firebaseDb.js'

const sentryDsn = process.env.REACT_APP_SENTRY_DSN

Sentry.init({
    dsn: sentryDsn,
    enabled: Boolean(sentryDsn),
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
    release: `farmdeck-frontend@${WEBAPP_VERSION}`,
    sendDefaultPii: false,
})

configureInterceptors()

createFirebaseInstance()

// ResizeObserver fires "loop completed with undelivered notifications" when a callback
// triggers a synchronous layout change (common with maps, MUI drawers, animation libs).
// Debouncing to rAF prevents the condition entirely without masking real errors.
if (typeof window !== 'undefined' && window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver
    window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
        constructor(callback) {
            super((entries, observer) => {
                window.requestAnimationFrame(() => callback(entries, observer))
            })
        }
    }
}

// React Refresh is handled automatically by Create React App 5+
// No need for manual module.hot.accept() calls

if (Capacitor.getPlatform() !== 'web') {
    if (Capacitor.getPlatform() === 'android') {
        Promise.all([StatusBar.setStyle({ style: Style.Default }), StatusBar.setOverlaysWebView({ overlay: false })]).then(() => {
            StatusBar.show()
        })
    } else {
        StatusBar.setStyle({ style: Style.Default }).then(() => {
            StatusBar.show()
        })
    }
}

const root = createRoot(document.getElementById('root'))

// REACT_APP_MAINTENANCE_MODE=true or 1 at build time: full-site upgrading screen (no routes). Unset or false for normal launch.
if (process.env.REACT_APP_MAINTENANCE_MODE === 'true' || process.env.REACT_APP_MAINTENANCE_MODE === '1') {
    root.render(<SiteUpgradingScreen />)
} else {
    root.render(
        <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
                <App />
            </PersistGate>
        </Provider>,
    )
}

console.info(`farmdeck:: v${process.env.REACT_APP_BUILD_MODE === 'web' ? WEBAPP_VERSION : process.env.REACT_APP_BUILD_MODE === 'ios' ? APP_IOS_VERSION : APP_ANDROID_VERSION} @ ${LAST_MODIFIED_TIMESTAMP}`)
console.info(`mode:: ${process.env.REACT_APP_BUILD_MODE}`)
console.info(`envr:: ${process.env.NODE_ENV}`)

//Stop all logs from shown in case of prod  builds
function noop() {}
if (process.env.NODE_ENV !== 'development') {
    if (window.localStorage.getItem('SHOW_DEBUG') !== '*') {
        console.log = noop
        console.warn = noop
    }
}
// {/* <StrictMode> */}
// <App />
// {/* </StrictMode> */}
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.unregister();
